import {
	Button, Stack, TextField, FormControl,
	InputLabel, Select, MenuItem, Tooltip, Accordion, AccordionSummary,
	AccordionDetails, IconButton
} from "@mui/material";
import { patchLocationsets } from "api/layer";
import Icon from "components/icon/icon";
import Modal from "components/modal/modal";
import { LeftPanel } from "helpers/constants";
import { AppAction, AppContext, setLeftPanel } from "helpers/context";
import { LocationData } from "helpers/models";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./edit-location-set.module.scss";

export default function EditLocationSet() {
	const [context, dispatch] = useContext(AppContext);
  const locationSets = context.locationSets;
	const [t] = useTranslation();
	const [expanded, setExpanded] = useState(true);
	const [disabled, setDisabled] = useState(false);
	const [formData, setFormData] = useState({ ...context.currentMultipleLocations });
	const [aggregationUnitOptions, setAggregationUnitOptions] = useState([{
		value: "numberOfLocations",
		name: "Number of Locations",
	}, {
		value: "total",
		name: "total",
	}, {
		value: "numberOfRisks",
		name: "Number of Risks",
	}, {
		value: "building",
		name: "Building",
	}, {
		value: "content",
		name: "Content",
	}, {
		value: "businessInterruption",
		name: "Business Interruption",
	}, {
		value: "propertyDamage",
		name: "Property Damage",
	}, {
		value: "limit",
		name: "Limit",
	}, {
		value: "deductible",
		name: "Deductible",
	}, {
		value: "other",
		name: "Other",
	}]);

	const handleChangeExpand = () => {
		setExpanded(!expanded);
	};

	const handleChange = (event: any) => {
		const { name, value } = event.target;
		setFormData((prevData) => ({ ...prevData, [name]: value }));
	};

	const save = () => {
		if (!formData.name || !formData.id) {
			dispatch({
				type: AppAction.addSnackBarParams,
				payload: {
					titleIcon: "error",
					titleIconColor: "red",
					title: "errorsInLocationSetForm",
					message: "pleaseFixTheErrorsLocationSet",
				}
			})
			return;
		}
		setDisabled(true);
		patchLocationsets(formData.id, formData).then((res: any) => {
			if (res.code !== 200 || !res.data) {
				return;
			}
			const name = res.data.name;
			const unit = res.data.aggregationUnit;
			dispatch({
				type: AppAction.addSnackBarParams,
				payload: {
					title: "editLocationSetTitle",
					messageData: name,
					message: "editLocationSetMessage",
				},
			});
			let updatedLocationSets = locationSets?.map((locationSet: LocationData) => {
				if (locationSet.id === formData.id) {
					locationSet = { ...locationSet, ...res.data };

					const currentUnit = context.currentMultipleLocations?.aggregationUnit;
					const hasItems = Boolean(locationSet.items?.length);
					const isValidUnit = unit && unit !== "numberOfLocations";
					const isDifferentUnit = unit !== currentUnit;
					// 主逻辑判断
					if (hasItems && isValidUnit && isDifferentUnit) {
						let hiddenLocationsArr: any = [];
						locationSet.items = locationSet.items?.map((item: any) => {
							// item.checked = item[unit] ? item.checked : false;
							if (!item[unit]) {
								hiddenLocationsArr.push(item)
							}
							return item
						})

						dispatch({
							type: AppAction.setCurrentHiddenLocationsModal,
							payload: {
								open: false,
								tableData: hiddenLocationsArr
							}
						})
						if (hiddenLocationsArr.length) {
							dispatch({
								type: AppAction.addSnackBarParams,
								payload: {
									titleIcon: "warning",
									titleIconColor: "#ffa02f",
									title: "locationsWereHidden",
									titleData: hiddenLocationsArr.length,
									message: "locationsWereHiddenMessage",
									messageData: t("locations." + unit),
									footBtn: () => {
										return <Button
											color="success"
											style={{ marginRight: '10px' }}
											onClick={() => {
												dispatch({
													type: AppAction.setCurrentHiddenLocationsModal,
													payload: {
														tableData: hiddenLocationsArr,
														open: true,
													}
												})
												dispatch({ type: AppAction.popSnackBarParams })
											}}>
											{t("buttons.viewDetails")}
										</Button>
									}
								}
							})
						}
					} else {
						dispatch({
							type: AppAction.setCurrentHiddenLocationsModal,
							payload: undefined
						})
					}
					dispatch({ type: AppAction.setCurrentMultipleLocations, payload: { ...locationSet } });
				}
				return locationSet;
			});
			dispatch({ type: AppAction.setLocationSets, payload: updatedLocationSets });
			setLeftPanel(dispatch, LeftPanel.Locations);
			setDisabled(false);
		})
	};

	const deleteSet = () => {
		dispatch({
			type: AppAction.setDeleteLocationSetModal,
			payload: context.currentMultipleLocations,
		});
	};

	const closeEdit = (): void => {
		setLeftPanel(dispatch, LeftPanel.Locations);
	};

	const aggregationUnitSellect = (disabled: boolean) => {
		return <FormControl fullWidth disabled={disabled}>
			<InputLabel color="success">{t("locations.createModal.aggregationUnit")}</InputLabel>
			<Select
				name="aggregationUnit"
				value={formData.aggregationUnit}
				color="success"
				label={t("locations.createModal.aggregationUnit")}
				MenuProps={{
					PaperProps: {
						style: {
							maxHeight: 300,
						},
					},
				}}
			>
				{aggregationUnitOptions.map((item) => {
					let disabledMenuItem = false;
					if (item.value != "numberOfLocations") {
						if (!context.currentMultipleLocations?.items || context.currentMultipleLocations.items.length === 0) {
							disabledMenuItem = true;
						} else {
							let isFind = context.currentMultipleLocations.items.find((location: any) => location[item.value]);
							if (!isFind) {
								disabledMenuItem = true;
							}
						}
					}
					return (disabledMenuItem ?
						<Tooltip title={t("locations.createModal.tooltip4")} placement="top" disableInteractive key={`tooltip-${item.value}`}>
							<span
								style={{
									display: "block",
									width: "100%",
								}}
							>
								<MenuItem value={item.value} disabled >{t("locations." + item.name)}</MenuItem>
							</span>
						</Tooltip>
						:
						<MenuItem
							value={item.value}
							onClick={() => {
								handleChange({ target: { name: "aggregationUnit", value: item.value } });
							}}
							key={item.value}
						>
							{t("locations." + item.name)}
						</MenuItem>)
				})}
			</Select>
		</FormControl>
	}

	return (
		<div className={styles.edit}>
			<div className={styles.header}>
				<div className={styles.title}>
					<div className="flex items-center overflow-hidden">
						<Icon
							icon="arrow_back"
							onClick={closeEdit}
						/>
						<span className="truncate">{t("locationSet.editTitle", { title: context.currentMultipleLocations?.name })}</span>
					</div>
					<div>
						<Icon onClick={() => setLeftPanel(dispatch, LeftPanel.Empty)} />
					</div>
				</div>
			</div>
			<div className={styles.body}>
				<Stack spacing={{ xs: 1, sm: 2 }}>
					<Stack
						direction="row"
						spacing={1}
						sx={{
							justifyContent: "flex-start",
							alignItems: "center",
						}}>
						<TextField
							required
							label={t("locationSet.edit.Location_set_name")}
							name="name"
							inputProps={{ maxLength: 180 }}
							variant="outlined"
							color="success"
							value={formData.name}
							onChange={handleChange}
							error={!formData.name}
							helperText={!formData.name && t("locations.editModal.nameText")}
							style={{ flex: 1 }}
						/>
						<Tooltip
							title={t("locations.createModal.tooltip1")}
							placement="top"
						>
							<IconButton style={{ marginLeft: "10px" }}
								onClick={(e) => {
									e.stopPropagation();
								}}
							>
								<span className="material-icons">info</span>
							</IconButton>
						</Tooltip>
					</Stack>
					<Stack
						direction="row"
						spacing={2}
						sx={{
							justifyContent: "flex-start",
							alignItems: "center",
							marginBottom: "20px",
						}}
					>
						<TextField
							label={t("locations.add.currency")}
							value={formData.currency}
							onChange={handleChange}
							name="currency"
							inputProps={{ maxLength: 20 }}
							color="success"
							style={{ width: "calc(100% - 50px)" }}
						/>
					</Stack>
					<p className={styles.title1}>{t("locations.createModal.locationDisplayOptions")}</p>
					<FormControl fullWidth style={{ margin: "5px 0 20px" }} disabled>
						<InputLabel color="success">{t("locations.createModal.locationSource")}</InputLabel>
						<Select
							name="locationSource"
							value={formData.locationSource}
							onChange={handleChange}
							color="success"
							label={t("locations.createModal.locationSource")}
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 300,
									},
								},
							}}
							style={{ width: "calc(100% - 50px)" }}
						>
							<MenuItem value="latlng">{t("locations.createModal.latlng")}</MenuItem>
							<MenuItem value="address">{t("locations.createModal.address")}</MenuItem>
						</Select>
					</FormControl>
					<Stack
						direction="row"
						spacing={1}
						sx={{
							justifyContent: "flex-start",
							alignItems: "center",
						}}>
						{
							context.currentMultipleLocations?.items?.length ?
								aggregationUnitSellect(false)
								:
								<Tooltip title={t("locations.createModal.tooltip5")} placement="top">
									{aggregationUnitSellect(true)}
								</Tooltip>
						}
						<Tooltip
							title={t("locations.createModal.tooltip2")}
							placement="top"
						>
							<IconButton style={{ marginLeft: "10px" }}
								onClick={(e) => {
									e.stopPropagation();
								}}
							>
								<span className="material-icons">info</span>
							</IconButton>
						</Tooltip>
					</Stack>
					<Accordion
						expanded={expanded}
						onChange={handleChangeExpand}
						sx={{
							boxShadow: "none",
							backgroundColor: "transparent",
							margin: "0!important",
							"&::before": {
								display: "none",
							},
						}}
					>
						<AccordionSummary
							expandIcon={<div className="material-icons">keyboard_arrow_right</div>}
							sx={{
								padding: 0,
								margin: 0,
								flexDirection: "row-reverse",
								"& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
									transform: "rotate(90deg)",
								},
								"& .MuiTypography-root": {
									fontFamily: "Roboto-Regular",
								},
								"&.Mui-expanded": {
									minHeight: "48px",
								},
								"& .MuiAccordionSummary-content.Mui-expanded": {
									margin: "12px 0",
								},
								"& .MuiAccordionSummary-content": {
									alignItems: "center",
								}
							}}
						>
							{t("locations.createModal.additionalInfo")}
							<Tooltip
								title={t("locations.createModal.tooltip3")}
								placement="top"
							>
								<IconButton style={{ marginLeft: "10px" }}
									onClick={(e) => {
										e.stopPropagation();
									}}
								>
									<span className="material-icons">info</span>
								</IconButton>
							</Tooltip>
						</AccordionSummary>
						<AccordionDetails
							sx={{
								padding: "6px 0 0",
								width: "100%",
							}}
						>
							<Stack spacing={{ xy: 1, }}>
								<TextField
									multiline
									label={t("locations.createModal.comment")}
									name="comment"
									value={formData.comment}
									onChange={handleChange}
									color="success"
									inputProps={{
										style: {
											resize: "vertical",
										},
										maxLength: 50
									}}
									rows={2}
									sx={{
										marginBottom: "15px",
									}}
								/>
							</Stack>
						</AccordionDetails>
					</Accordion>
				</Stack>
			</div>
			<div className={styles.footer}>
				<div>
					<Button
						variant="contained"
						disabled={disabled}
						onClick={save}
						color="success"
						style={{ marginRight: "10px" }}
					>
						{t("buttons.save")}
					</Button>
				</div>
				<Button
					variant="text"
					onClick={deleteSet}
					color="error"
				>
					{t("buttons.delete")}
				</Button>
			</div>
		</div>
	);
}

